// EndpointConfigStatePanel — the right-panel detail for one endpoint on an
// endpoint-subject signal scope. Where a prompt-subject signal opens the trace
// transcript, an endpoint-subject one opens the endpoint's AI-agent config.
//
// Ordered by what an analyst needs, in order: which machine is this, what fired
// and why, what does the config say now, and what changed to get here. The
// findings arrive as a prop — the parent lens already read them to build the
// endpoint list, so re-querying would be a second round-trip for rows we hold.
//
// Reads over REST (GetEndpointConfigState / GetEndpointMetadata), not the
// analytics graph — matching every other per-entity state/history panel here, and
// because the analytics planner caps absolute ranges at 90 days while the
// current-state projection has no TTL (a stable endpoint's last observation can be
// older than any window).

import { useEffect, useMemo, useState } from "react";
import ScrollArea from "../ScrollArea.jsx";
import CodeEditor from "../CodeEditor.jsx";
import { NOTE_CLASS } from "../../lib/ui.js";
import { Chip, DetailField, DetailSection, DetailRule } from "../detailPrimitives.jsx";
import { getEndpointConfigState, isWriterAttributed, lastAttributedWrite } from "../../lib/endpointConfigApi.js";
import { getEndpointMetadata } from "../../lib/reportClient.js";
import { uuidToEndpointId, flattenInventoryRow } from "../../lib/inventoryRows.js";
import {
  findingLabel,
  kindSummary,
  rollupFindingKinds,
  whyItFired,
  whatToCheck,
  writerLabel,
  otelKeyImpact,
  sameLayer,
  layerDisplay,
  operationDisplay,
  observationSourceDisplay,
  parseDiffEntry,
  touchedOtelKeys,
} from "../../lib/configShadowFindings.js";

const HISTORY_LIMIT = 200;

// Targets listed under a finding kind before the rest collapse behind an
// expander. One kind can carry hundreds of targets when the observed path varies
// per write, and an unbounded list here pushes the config body and the change
// timeline — the sections that answer "what does it actually say" — off the
// bottom of the panel. Targets are newest-first, so the preview is the slice
// worth triaging.
const TARGET_PREVIEW_LIMIT = 5;

function fmtTime(iso) {
  const ms = typeof iso === "number" ? iso : Date.parse(iso ?? "");
  if (!Number.isFinite(ms)) return "";
  return new Date(ms).toLocaleString();
}

function shortHash(h) {
  return h ? h.slice(0, 12) : "";
}

// A row whose content hash and existence both match the previous observation and
// which changed no keys is a pure re-read — the agent re-emits an unchanged
// layer's baseline so a dropped changelog row self-heals. Noise in a timeline, so
// collapse them behind a count.
function isNoOpReRead(c) {
  return (
    !c.addedKeys.length &&
    !c.updatedKeys.length &&
    !c.removedKeys.length &&
    c.exists === c.changePreviousExists &&
    c.contentHash === c.changePreviousContentHash
  );
}

// Pretty-print the config body. It arrives as a JSON string; re-indenting makes it
// readable, but a body that doesn't parse must still be shown verbatim rather than
// replaced by an error — the agent bounds content in-band, so odd shapes are
// expected, not exceptional.
function formatBody(raw) {
  if (!raw) return "";
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

// The agent replaces dropped subtrees with `truncation_marker` objects rather than
// omitting them, so a body can be structurally complete and still be missing
// content. Say so instead of letting the reader assume they see everything.
function isTruncated(raw) {
  return typeof raw === "string" && raw.includes("truncation_marker");
}

const DIFF_KIND_LABEL = { added: "added", updated: "changed", removed: "removed" };

// One structured diff row. The agent ships pre-rendered strings, so `before` /
// `after` may be absent (a collapsed subtree names an object, not a leaf).
function DiffRow({ entry }) {
  const otel = touchedOtelKeys(entry.pointer + " " + (entry.before ?? "") + " " + (entry.after ?? ""));
  return (
    <div className="grid gap-[2px] min-w-0">
      <span className="flex flex-wrap items-baseline gap-[6px] min-w-0">
        <span className="text-[10px] uppercase tracking-[0.06em] text-[color:var(--muted-soft)]">
          {DIFF_KIND_LABEL[entry.kind] || entry.kind}
        </span>
        <span className="font-mono text-[11px] text-[color:var(--ink)] break-all">{entry.pointer}</span>
        {otel.map((k) => (
          <Chip key={k} title={otelKeyImpact(k)}>
            {k}
          </Chip>
        ))}
      </span>
      {entry.before !== undefined || entry.after !== undefined ? (
        <span className="font-mono text-[10px] leading-[1.45] text-[color:var(--muted)] break-all">
          {entry.before !== undefined ? <span className="line-through">{entry.before}</span> : null}
          {entry.before !== undefined && entry.after !== undefined ? " → " : null}
          {entry.after !== undefined ? <span className="text-[color:var(--ink)]">{entry.after}</span> : null}
        </span>
      ) : null}
    </div>
  );
}

// Several paths in one field. A comma-joined string does not survive here: these
// are long enough to wrap over three or four lines each, which swallows the
// separator and reads as one impossibly-long path. So each gets its own bulleted
// row, and the label carries the count.
function PathList({ label, values = [] }) {
  const items = values.filter(Boolean);
  if (items.length <= 1) return <DetailField label={label} value={items[0] || ""} mono />;
  return (
    <div className="grid gap-[2px] min-w-0 content-start">
      <span className={`${NOTE_CLASS} text-[10px] uppercase tracking-[0.06em]`}>
        {label}s ({items.length})
      </span>
      <ul className="grid gap-[4px] min-w-0">
        {items.map((v) => (
          <li key={v} className="flex gap-[5px] min-w-0 text-[12px] leading-[1.32]">
            <span className="shrink-0 text-[color:var(--muted-soft)]">·</span>
            <span className="font-mono break-all text-[color:var(--ink)]" title={v}>{v}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function diffEntries(change) {
  return [
    ...change.addedKeys.map((e) => parseDiffEntry(e, "added")),
    ...change.updatedKeys.map((e) => parseDiffEntry(e, "updated")),
    ...change.removedKeys.map((e) => parseDiffEntry(e, "removed")),
  ];
}

export default function EndpointConfigStatePanel({ endpointId, hostname, findings = [], range }) {
  const wireId = useMemo(() => uuidToEndpointId(endpointId), [endpointId]);

  const [state, setState] = useState(null);
  const [stateErr, setStateErr] = useState(null);
  const [meta, setMeta] = useState(null);
  // Which config target's body + timeline are shown. Defaults to the newest
  // finding's target once state lands.
  const [selectedKey, setSelectedKey] = useState(findings[0]?.key ?? null);
  // Kinds whose full target list the reader has asked for, by tag.
  const [expandedKinds, setExpandedKinds] = useState(() => new Set());

  const kinds = useMemo(() => rollupFindingKinds(findings), [findings]);

  useEffect(() => {
    if (!endpointId) return undefined;
    let cancelled = false;
    setState(null);
    setStateErr(null);
    setMeta(null);
    getEndpointConfigState(wireId, { historyLimit: HISTORY_LIMIT })
      .then((res) => { if (!cancelled) setState(res); })
      .catch((e) => { if (!cancelled) setStateErr(e); });
    // Endpoint identity is best-effort: without it the panel still works, it just
    // shows the id instead of a hostname, so a failure here stays silent.
    getEndpointMetadata(wireId)
      .then((res) => {
        if (cancelled) return;
        const info = res?.metadata ?? res;
        setMeta(info ? flattenInventoryRow(info) : null);
      })
      .catch(() => { if (!cancelled) setMeta(null); });
    return () => { cancelled = true; };
    // `range` is deliberately not a dep: current state is unwindowed by design and
    // the history window is the server default, so a range nudge changes nothing
    // this panel shows.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpointId, wireId]);

  useEffect(() => {
    setSelectedKey(findings[0]?.key ?? null);
    setExpandedKinds(new Set());
  }, [findings]);

  const loading = !state && !stateErr;
  const current = state?.current || [];
  const history = state?.history || [];

  // Match a finding to its config target. The tag value carries no
  // config_target_id, but (layer, source_path) is the target identity as a tag
  // sees it — config_target_id is itself derived from agent + layer + scope.
  //
  // `sameLayer`, not `===`: the tag spells the layer `ManagedFile` while the
  // config-state row spells it `SETTINGS_LAYER_MANAGED_FILE`, so raw equality
  // never matches and every finding would look like it has no config target.
  const targetFor = (finding) =>
    current.find((t) => sameLayer(t.layer, finding?.layer) && t.sourcePath === finding?.sourcePath) || null;

  const selected = findings.find((f) => f.key === selectedKey) || findings[0] || null;
  const selectedTarget = selected ? targetFor(selected) : null;

  // Timeline for the selected target only. Filtered client-side from the window
  // already fetched; when nothing is selected, show the endpoint's whole history.
  const timeline = useMemo(() => {
    const rows = selectedTarget
      ? history.filter((c) => c.configTargetId === selectedTarget.configTargetId)
      : history;
    const shown = rows.filter((c) => isWriterAttributed(c) || !isNoOpReRead(c));
    return { shown, suppressed: rows.length - shown.length, lastWrite: lastAttributedWrite(shown) };
  }, [history, selectedTarget]);

  const body = selectedTarget ? formatBody(selectedTarget.configContentJson) : "";

  return (
    <ScrollArea className="min-h-0 h-full" contentClassName="grid gap-[14px] px-[2px] py-[8px] min-w-0" aria-busy={loading}>
      {/* Endpoint identity. The host header already renders the title, so this
          carries the attributes that title can't. */}
      <DetailSection title="Endpoint">
        <DetailField label="Hostname" value={meta?.hostname || hostname || ""} />
        <DetailField label="Operating system" value={meta?.platform || ""} />
        <DetailField label="Agent version" value={meta?.agentVersion || ""} />
        <DetailField label="Registered user" value={meta?.registeredUser || ""} />
        <DetailField label="Last reported" value={meta?.snapshotTime ? fmtTime(meta.snapshotTime) : ""} />
        <DetailField label="Endpoint id" value={endpointId} mono />
      </DetailSection>

      <DetailRule />

      {/* Findings. One entry per distinct problem, not per observation. */}
      <DetailSection
        title={`Findings (${findings.length})`}
        columns={1}
        note={findings.length ? "" : "No findings carried for this endpoint."}
      >
        {/* Grouped by finding kind, then by config target within it. Only the
            selected target expands: three findings' worth of prose and bullets
            already pushed the config body and the change timeline off the bottom
            of the panel, and a kind carrying hundreds of targets buries them
            outright. Selecting a target also re-points the sections below, so the
            collapse and the drill-down are one gesture. */}
        <div className="grid gap-[14px] min-w-0">
          {kinds.map((kind) => {
            const expanded = expandedKinds.has(kind.tag);
            const visible = expanded ? kind.targets : kind.targets.slice(0, TARGET_PREVIEW_LIMIT);
            const hidden = kind.targets.length - visible.length;
            return (
              <div key={kind.tag} className="grid gap-[8px] min-w-0" data-finding-kind={kind.tag}>
                <span className="flex flex-wrap items-baseline gap-[6px] min-w-0">
                  <span className="text-[11px] text-[color:var(--ink)]">{kindSummary(kind)}</span>
                  {kind.lastSeenMs ? (
                    <span className={`${NOTE_CLASS} text-[10px]`}>last {fmtTime(kind.lastSeenMs)}</span>
                  ) : null}
                </span>
                <div className="grid gap-[10px] min-w-0">
                  {visible.map((f) => {
                    const isSelected = f.key === selectedKey;
                    const checks = whatToCheck(f.tag);
                    return (
                      <div
                        key={f.key}
                        className={`grid gap-[6px] min-w-0 border-l-2 pl-[8px] ${isSelected ? "border-[color:var(--line-strong)]" : "border-[color:var(--line-soft)]"}`}
                      >
                        <button
                          type="button"
                          onClick={() => setSelectedKey(f.key)}
                          className="flex flex-wrap items-baseline gap-[6px] text-left cursor-pointer min-w-0"
                          data-finding-key={f.key}
                          aria-expanded={isSelected}
                        >
                          {/* The kind heading above already names what fired, so the row
                              carries what distinguishes one target from its siblings: the
                              path. Full, not elided — sibling targets can differ only in
                              a middle segment. */}
                          <span
                            className={`font-mono text-[10px] break-all ${isSelected ? "text-[color:var(--ink)]" : "text-[color:var(--muted)]"}`}
                            title={f.sourcePath}
                          >
                            {f.sourcePath || findingLabel(f.tag)}
                          </span>
                          {f.count > 1 ? <Chip>×{f.count} observations</Chip> : null}
                          {f.lastSeenMs ? (
                            <span className={`${NOTE_CLASS} text-[10px]`}>last {fmtTime(f.lastSeenMs)}</span>
                          ) : null}
                        </button>

                        {isSelected ? (
                          <>
                            <span className="text-[11px] leading-[1.5] text-[color:var(--muted)] break-words">
                              {whyItFired(f.tag, f.detail)}
                            </span>

                            <div className="grid gap-[8px] min-w-0 [grid-template-columns:repeat(2,minmax(0,1fr))]">
                              <DetailField label="Layer" value={f.layer} />
                              <DetailField label="Operation" value={(f.operations || []).join(", ")} />
                              <DetailField label="Source path" value={f.sourcePath} mono />
                              <PathList label="Writer" values={(f.writers || []).map(writerLabel)} />
                              {f.firstSeenMs && f.firstSeenMs !== f.lastSeenMs ? (
                                <DetailField label="First seen" value={fmtTime(f.firstSeenMs)} />
                              ) : null}
                            </div>

                            {f.otelKeys?.length ? (
                              <div className="grid gap-[3px] min-w-0">
                                <span className={`${NOTE_CLASS} text-[10px] uppercase tracking-[0.06em]`}>Telemetry keys</span>
                                {f.otelKeys.map((k) => (
                                  <span key={k} className="text-[10px] leading-[1.45] text-[color:var(--muted)] break-words">
                                    <span className="font-mono text-[color:var(--ink)]">{k}</span>
                                    {otelKeyImpact(k) ? `: ${otelKeyImpact(k)}` : ""}
                                  </span>
                                ))}
                              </div>
                            ) : null}

                            {checks.length ? (
                              <div className="grid gap-[3px] min-w-0">
                                <span className={`${NOTE_CLASS} text-[10px] uppercase tracking-[0.06em]`}>What to check</span>
                                <ul className="grid gap-[3px] pl-[14px] list-disc">
                                  {checks.map((c) => (
                                    <li key={c} className="text-[10px] leading-[1.45] text-[color:var(--muted)] break-words">{c}</li>
                                  ))}
                                </ul>
                              </div>
                            ) : null}
                          </>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
                {hidden > 0 ? (
                  <button
                    type="button"
                    onClick={() => setExpandedKinds((prev) => new Set(prev).add(kind.tag))}
                    className={`${NOTE_CLASS} justify-self-start text-[10px] cursor-pointer underline decoration-dotted`}
                    data-expand-kind={kind.tag}
                  >
                    Show {hidden} more target{hidden === 1 ? "" : "s"}
                  </button>
                ) : null}
              </div>
            );
          })}
        </div>
      </DetailSection>

      {stateErr ? (
        <span className={NOTE_CLASS}>
          {stateErr.status === 403
            ? "You don’t have permission to read this endpoint’s config state."
            : "Couldn’t load this endpoint’s config state. Try again shortly."}
        </span>
      ) : null}

      <DetailRule />

      {/* The selected target's current state and body. */}
      <DetailSection
        title={selected ? `Config now: ${findingLabel(selected.tag)}` : "Config now"}
        busy={loading}
        note={
          loading
            ? "Reading current config state…"
            : stateErr
              ? ""
              : !current.length
                ? "No config targets observed on this endpoint."
                : !selectedTarget
                  ? "No current state was found for this config target. The layer may have been removed."
                  : ""
        }
      >
        {selectedTarget ? (
          <>
            <DetailField label="Layer" value={layerDisplay(selectedTarget.layer)} />
            <DetailField label="Exists" value={selectedTarget.exists ? "yes" : "no"} />
            <DetailField label="Source path" value={selectedTarget.sourcePath} mono />
            <DetailField label="Content hash" value={shortHash(selectedTarget.contentHash)} mono />
            <DetailField label="Provider" value={selectedTarget.agentProvider} />
            <DetailField label="Observed" value={fmtTime(selectedTarget.observedAt)} />
          </>
        ) : null}
      </DetailSection>

      {selectedTarget ? (
        <DetailSection
          title="Current config content"
          columns={1}
          note={
            !body
              ? "This target reported no content."
              : isTruncated(body)
                ? "The agent truncated this content. `truncation_marker` nodes replace omitted subtrees."
                : ""
          }
        >
          {body ? (
            <div data-testid="config-body" className="min-w-0 overflow-hidden">
              <CodeEditor value={body} onChange={() => {}} language="json" readOnly minHeight="140px" maxHeight="40vh" />
            </div>
          ) : null}
        </DetailSection>
      ) : null}

      <DetailRule />

      {/* Who last actually wrote it — not simply the newest row. */}
      <DetailSection
        title="Last attributed write"
        busy={loading}
        note={
          timeline.lastWrite || loading
            ? ""
            : "No write was attributed in this window. All observations were scans or polls."
        }
      >
        {timeline.lastWrite ? (
          <>
            <DetailField label="Writer" value={writerLabel(timeline.lastWrite.writerImagePath)} mono />
            <DetailField label="Operation" value={operationDisplay(timeline.lastWrite.writerOperation)} />
            <DetailField label="Layer" value={layerDisplay(timeline.lastWrite.layer)} />
            <DetailField label="When" value={fmtTime(timeline.lastWrite.observedAt)} />
          </>
        ) : null}
      </DetailSection>

      {/* Change timeline for the selected target. */}
      <DetailSection
        title={`Changes (${timeline.shown.length})`}
        columns={1}
        busy={loading}
        note={
          loading
            ? "Reading config history…"
            : stateErr
              ? ""
              : timeline.shown.length
                ? timeline.suppressed > 0
                  ? `${timeline.suppressed} unchanged re-read${timeline.suppressed === 1 ? "" : "s"} hidden.`
                  : ""
                : "No config changes observed in this window."
        }
      >
        <div className="grid gap-[12px]" data-testid="config-timeline">
          {timeline.shown.map((c) => {
            const entries = diffEntries(c);
            return (
              <div key={c.eventId} className="grid gap-[4px] border-l-2 border-[color:var(--line-soft)] pl-[8px]">
                <span className="flex flex-wrap items-baseline gap-[6px] min-w-0">
                  <span className="text-[10px] text-[color:var(--muted)] shrink-0">{fmtTime(c.observedAt)} · {layerDisplay(c.layer)}</span>
                  {/* A writer image path can be a 120-char framework path, so it
                      needs break-all — without it the row sets the panel's width
                      and clips every sibling section at the edge. */}
                  <span className="text-[11px] text-[color:var(--ink)] min-w-0 break-all">
                    {isWriterAttributed(c)
                      ? `${writerLabel(c.writerImagePath)}${c.writerOperation ? ` · ${operationDisplay(c.writerOperation)}` : ""}`
                      : observationSourceDisplay(c.observationSource) || "observation"}
                  </span>
                  {c.exists ? null : <Chip>removed</Chip>}
                </span>
                {entries.length ? (
                  <div className="grid gap-[5px] min-w-0">
                    {entries.map((e, i) => <DiffRow key={`${e.kind}:${e.pointer}:${i}`} entry={e} />)}
                  </div>
                ) : (
                  <span className={`${NOTE_CLASS} text-[10px]`}>No key-level changes recorded.</span>
                )}
              </div>
            );
          })}
        </div>
      </DetailSection>
    </ScrollArea>
  );
}
